(() => {
  if (customElements.get('product-cards-carousel')) return;

  class ProductCardsCarousel extends HTMLElement {
    constructor() {
      super();
      this.swiper = null;
      this.onWindowLoad = this.onWindowLoad.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onBlockSelect = this.onBlockSelect.bind(this);
    }

    connectedCallback() {
      this.sliderEl = this.querySelector('[data-slider]');
      this.prevButton = this.querySelector('[data-prev]');
      this.nextButton = this.querySelector('[data-next]');
      if (!this.sliderEl) return;

      window.addEventListener('resize', this.onResize);
      if (window.Shopify && Shopify.designMode) {
        document.addEventListener('shopify:block:select', this.onBlockSelect);
      }

      if (typeof Swiper === 'undefined') {
        if (document.readyState === 'complete') {
          this.initFallback();
        } else {
          window.addEventListener('load', this.onWindowLoad, { once: true });
        }
        return;
      }

      this.initSwiper();
    }

    disconnectedCallback() {
      window.removeEventListener('load', this.onWindowLoad);
      window.removeEventListener('resize', this.onResize);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      this.destroySwiper();
    }

    onWindowLoad() {
      if (typeof Swiper === 'undefined') {
        this.initFallback();
        return;
      }
      this.initSwiper();
    }

    onResize() {
      this.updateNavPosition();
    }

    onBlockSelect(event) {
      if (!this.contains(event.target)) return;
      const slides = Array.from(this.sliderEl.querySelectorAll('.swiper-slide'));
      const index = slides.findIndex((slide) => slide.contains(event.target));
      if (index < 0) return;

      if (this.swiper) {
        this.swiper.slideTo(index);
      } else {
        slides[index].scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
      }
    }

    // Center the arrows on the product image rather than on the whole card.
    updateNavPosition() {
      const media = this.querySelector('[data-media]');
      if (!media) return;
      const top = media.getBoundingClientRect().top - this.getBoundingClientRect().top;
      this.style.setProperty('--pcc-nav-top', `${top + media.offsetHeight / 2}px`);
    }

    initSwiper() {
      if (this.swiper) return;

      const slideCount = this.sliderEl.querySelectorAll('.swiper-slide').length;
      if (!slideCount) return;

      this.swiper = new Swiper(this.sliderEl, {
        slidesPerView: 1.41,
        spaceBetween: 18,
        slidesOffsetAfter: 0,
        breakpointsInverse: true,
        watchOverflow: true,
        grabCursor: true,
        navigation: {
          prevEl: this.prevButton,
          nextEl: this.nextButton,
        },
        breakpoints: {
          600: {
            slidesPerView: 2.2,
            spaceBetween: 20,
          },
          768: {
            slidesPerView: 2.4,
            spaceBetween: 20,
          },
          1024: {
            slidesPerView: 3,
            spaceBetween: 24,
          },
          1200: {
            slidesPerView: 4,
            spaceBetween: 24,
          },
        },
        on: {
          resize: () => this.updateNavPosition(),
        },
      });

      this.classList.add('is-swiper');
      requestAnimationFrame(() => {
        this.swiper?.update();
        this.updateNavPosition();
        this.classList.add('is-initialized');
      });
    }

    // Swiper is only loaded when the theme's new style is enabled; fall back to native scrolling.
    initFallback() {
      this.classList.add('is-fallback', 'is-initialized');
    }

    destroySwiper() {
      if (!this.swiper) return;
      this.swiper.destroy(true, true);
      this.swiper = null;
      this.classList.remove('is-swiper', 'is-initialized');
    }
  }

  customElements.define('product-cards-carousel', ProductCardsCarousel);
})();
